import React from 'react';
import styled from '@emotion/styled';
import { Card } from '../Card';
import Button from '../Button';
import Headline3 from '../Typography/Heading/Headline3';
import FeatureCheckList from '../FeatureCheckList';
import breakpoints from '../Theme/breakpoints';
import colors from '../Theme/colors';
import grey from '../Palettes/grey';

const TierGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin: 2rem 0;

  @media (min-width: ${breakpoints.md}px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas: 'monthly company yearly';
  }
`;

const TierCard = styled(Card)`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  border: 2px solid ${grey[300]};
`;

const MonthlyTierCard = styled(TierCard)`
  @media (min-width: ${breakpoints.md}px) {
    grid-area: monthly;
  }
`;

const CompanyTierCard = styled(TierCard)`
  border-color: ${colors.black};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);

  @media (min-width: ${breakpoints.md}px) {
    grid-area: company;
  }
`;

const YearlyTierCard = styled(TierCard)`
  @media (min-width: ${breakpoints.md}px) {
    grid-area: yearly;
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  height: 28px;
  border-radius: 14px;
  padding: 0 16px;
  background-color: ${colors.black};
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const TierCardContent = styled.div`
  padding: 24px;
`;

const HighlightedTierCardContent = styled(TierCardContent)`
  padding-top: 32px;
`;

const TierTitle = styled.h4`
  font-weight: 800;
  font-size: 1.25rem;
  text-align: center;
  margin: 0;
`;

const PriceDisplay = styled.div`
  text-align: center;
  margin: 16px 0;
`;

const PriceAmount = styled.span`
  font-size: 2rem;
  font-weight: bold;
  color: ${colors.black};
`;

const PriceDenominator = styled.span`
  font-size: 1rem;
  font-weight: bold;
  color: ${grey[600]};
`;

const BillingDescription = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: ${grey[600]};
  margin: 0;
`;

const SavingsBadge = styled.div`
  text-align: center;
  font-size: 0.875rem;
  font-weight: bold;
  color: ${colors.discount};
`;

const CtaContainer = styled.div`
  margin-top: auto;
  padding: 16px;
`;

const StyledHeadline3 = styled(Headline3)`
  && {
    text-align: center;
    margin-bottom: 1rem;
  }
`;

interface TierProperties {
  title: string;
  priceLabel: string;
  priceDenominator?: string;
  billingDescription: string;
  savingsMessage?: string;
  features: string[];
}

interface SubscriptionTierComparisonProperties {
  headingMessage: string;
  monthlyTier: TierProperties;
  yearlyTier: TierProperties;
  companyTier: Omit<TierProperties, 'priceDenominator' | 'savingsMessage'>;
  recommendedBadgeMessage: string;
  subscribeButtonLabel: string;
  contactSalesButtonLabel: string;
  MonthlyLinkComponent: React.ElementType;
  YearlyLinkComponent: React.ElementType;
  ContactSalesLinkComponent: React.ElementType;
}

const SubscriptionTierComparison = ({
  headingMessage,
  monthlyTier,
  yearlyTier,
  companyTier,
  recommendedBadgeMessage,
  subscribeButtonLabel,
  contactSalesButtonLabel,
  MonthlyLinkComponent,
  YearlyLinkComponent,
  ContactSalesLinkComponent,
}: SubscriptionTierComparisonProperties) => (
  <div>
    <StyledHeadline3>{headingMessage}</StyledHeadline3>
    <TierGrid>
      <CompanyTierCard>
        <RecommendedBadge>
          {recommendedBadgeMessage}
        </RecommendedBadge>
        <HighlightedTierCardContent>
          <TierTitle>{companyTier.title}</TierTitle>
          <PriceDisplay>
            <PriceAmount>{companyTier.priceLabel}</PriceAmount>
          </PriceDisplay>
          <BillingDescription>
            {companyTier.billingDescription}
          </BillingDescription>
          <FeatureCheckList features={companyTier.features} />
        </HighlightedTierCardContent>
        <CtaContainer>
          <ContactSalesLinkComponent>
            <Button variant="contained" fullWidth>
              {contactSalesButtonLabel}
            </Button>
          </ContactSalesLinkComponent>
        </CtaContainer>
      </CompanyTierCard>

      <MonthlyTierCard>
        <TierCardContent>
          <TierTitle>{monthlyTier.title}</TierTitle>
          <PriceDisplay>
            <PriceAmount>{monthlyTier.priceLabel}</PriceAmount>
            {monthlyTier.priceDenominator && (
              <PriceDenominator>
                /
                {monthlyTier.priceDenominator}
              </PriceDenominator>
            )}
          </PriceDisplay>
          <BillingDescription>
            {monthlyTier.billingDescription}
          </BillingDescription>
          <FeatureCheckList features={monthlyTier.features} />
        </TierCardContent>
        <CtaContainer>
          <MonthlyLinkComponent>
            <Button variant="outlined" fullWidth>
              {subscribeButtonLabel}
            </Button>
          </MonthlyLinkComponent>
        </CtaContainer>
      </MonthlyTierCard>

      <YearlyTierCard>
        <TierCardContent>
          <TierTitle>{yearlyTier.title}</TierTitle>
          <PriceDisplay>
            <PriceAmount>{yearlyTier.priceLabel}</PriceAmount>
            {yearlyTier.priceDenominator && (
              <PriceDenominator>
                /
                {yearlyTier.priceDenominator}
              </PriceDenominator>
            )}
          </PriceDisplay>
          <BillingDescription>
            {yearlyTier.billingDescription}
          </BillingDescription>
          {yearlyTier.savingsMessage && (
            <SavingsBadge>{yearlyTier.savingsMessage}</SavingsBadge>
          )}
          <FeatureCheckList features={yearlyTier.features} />
        </TierCardContent>
        <CtaContainer>
          <YearlyLinkComponent>
            <Button variant="outlined" fullWidth>
              {subscribeButtonLabel}
            </Button>
          </YearlyLinkComponent>
        </CtaContainer>
      </YearlyTierCard>
    </TierGrid>
  </div>
);

export default SubscriptionTierComparison;
